import React from 'react';
import { motion } from 'framer-motion';
import { BRAND } from '@/styles/brand-tokens';
import { authorLine, getBookCoverSrc } from '@/components/smchs/collection-lib';
import type { Book } from '@/services/catalog-api';

/* =====================================================================
 * Collection primitives: the "living collection" editorial vocabulary
 * shared by the public pages (Index, Catalog, …).
 *
 * Radius rule: panels/covers rounded-md (6px) with hairline borders,
 * interactive controls rounded-lg, tag chips rounded-full. Mono
 * small-caps are reserved for data metadata (dates, genres, counts);
 * panel titles are sentence case.
 * ===================================================================== */

/* Shared viewport-reveal wrapper: one motion vocabulary across pages. */
export const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.6, delay, ease: 'easeOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

/* =====================================================================
 * BOOK COVER: real cover art with a typographic-spine fallback.
 * The fallback stays inside the brand (deep navy ground, gold rule).
 * ===================================================================== */

export const BookCover: React.FC<{ book: Book; className?: string }> = ({
  book,
  className = '',
}) => {
  const src = getBookCoverSrc(book);
  return (
    <div
      className={`relative overflow-hidden rounded-md bg-[#101B38] text-[#F0EBE0] shadow-[inset_-3px_0_0_rgba(0,0,0,0.28)] ${className}`}
    >
      {/* Typographic spine: visible until/unless a cover loads */}
      <div className="absolute inset-0 flex flex-col justify-between p-3">
        <span className="block w-5 h-px bg-[var(--gold)]" />
        <div>
          <div className="font-serif italic text-[13px] leading-[1.25] line-clamp-4">
            {book.title}
          </div>
          <div className="mt-1.5 font-mono text-[8px] uppercase tracking-[0.14em] text-[#F0EBE0]/55 line-clamp-1">
            {authorLine(book)}
          </div>
        </div>
      </div>
      {src && (
        <img
          src={src}
          alt={`${book.title} cover`}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      {/* Spine edge over whatever renders */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-black/25" />
    </div>
  );
};

/* =====================================================================
 * Page-scoped tokens + keyframes. Status colours come from BRAND so
 * the palette stays single-sourced (no raw hex in page markup).
 * ===================================================================== */

export const CollectionStyle: React.FC = () => (
  <style>{`
    .smchs-home {
      --danger: ${BRAND.status.danger.light};
      --success: ${BRAND.status.success.light};
      --on-gold: ${BRAND.onGold};
    }
    .dark .smchs-home {
      --danger: ${BRAND.status.danger.dark};
      --success: ${BRAND.status.success.dark};
    }
    .smchs-grain {
      opacity: 0.05;
      mix-blend-mode: multiply;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E");
    }
    .dark .smchs-grain {
      opacity: 0.14;
      mix-blend-mode: soft-light;
    }
    @keyframes shelf-drift {
      from { transform: translateX(0); }
      to { transform: translateX(-50%); }
    }
    .shelf-track {
      animation: shelf-drift 55s linear infinite;
    }
    .shelf-track:hover {
      animation-play-state: paused;
    }
  `}</style>
);

/* Paper grain: fixed and inert; z-[70] sits above the sticky nav (z-40)
   and drawers (z-50) on purpose so the texture is uniform. */
export const GrainOverlay: React.FC = () => (
  <div aria-hidden className="smchs-grain fixed inset-0 z-[70] pointer-events-none" />
);
